import React, { useContext, useState } from "react"
import { isMobile } from "react-device-detect"
import { UserContext } from "../../HomeApp"
import { ShareUrlButton } from "../../ShareUrlButton"
import PreviewPlayer from "../../PreviewPlayer"
import {
  FollowComponent,
  FollowComponentLoggedOut,
} from "../../shared/FollowComponent"
import { BandSocialIcons } from "../../bands/BandSocialIcons"
import { Backline } from "../../Backline"
import type { Band } from "../../../types/bands"

interface ScrollerBandHeaderProps {
  band: Band
  headerMode?: "classic" | string
  showBackline?: boolean
  setShowBackline?: (val: boolean) => void
}

const getBandSubtitle = (band: Band) => {
  const isSecretVenue =
    band.band_type === "venue" &&
    band.additional_info?.is_location_secret &&
    band.additional_info.is_location_secret !== "no"
  const typeLabel = isSecretVenue ? "secret venue" : band.band_type_nice

  if (!band.location?.displayName) return typeLabel
  if (isSecretVenue) {
    const city =
      band.location.city ||
      band.location.neighborhood ||
      band.location.sublocality
    return city ? `${typeLabel} in ${city}` : typeLabel
  }

  const preposition = band.band_type === "venue" ? "in" : "from"
  return `${typeLabel} ${preposition} ${band.location.displayName}`
}

const ScrollerBandHeader: React.FC<ScrollerBandHeaderProps> = ({
  band,
  headerMode,
  showBackline: showBacklineProp,
  setShowBackline: setShowBacklineProp,
}) => {
  const { user } = useContext(UserContext)

  const bandUrl = `${window.location.protocol}//${window.location.host}${band.band_path}`
  const isFan = band.band_type === "fan"

  const urlParams = new URLSearchParams(window.location.search)
  const [showBacklineLocal, setShowBacklineLocal] = useState(
    urlParams.get("backline") === "true",
  )
  const showBackline =
    showBacklineProp !== undefined ? showBacklineProp : showBacklineLocal
  const setShowBackline = setShowBacklineProp ?? setShowBacklineLocal
  const hasBackline =
    band.band_type === "venue" && !!band.backline_offerings?.trim()

  return (
    <>
      <div
        className="flex justify-between overflow-hidden text-white px-3 lg:px-4 py-2"
        style={{
          backgroundColor: "rgb(0 0 0 / var(--header-solid, 0))",
        }}
      >
        <div className="flex min-w-0 flex-1">
          <div className="lg:text-right relative my-2 shrink-0">
            <PreviewPlayer
              bandId={band.id}
              width="4rem"
              height="4rem"
              className="text-white mr-2"
            />
          </div>

          <div className="min-w-0 flex-1 py-2">
            <h1 className="min-w-0 max-w-full font-extrabold text-xl lg:text-3xl uppercase leading-none">
              <span className="block truncate" title={band.name}>
                {band.name}
              </span>
              <span className="block truncate font-normal text-sm">
                {getBandSubtitle(band)}
              </span>
            </h1>

            {band.bio && (
              <div className="hidden lg:block text-sm italic leading-tight pt-1">
                <span
                  className="overflow-hidden"
                  style={{
                    display: "-webkit-box",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: 2,
                  }}
                >
                  {band.bio}
                </span>
              </div>
            )}

            {hasBackline && (
              <button
                onClick={() => setShowBackline(!showBackline)}
                className="text-blue-300 font-bold focus:outline-none"
                style={{ textShadow: "0px 0px 20px #ffffff" }}
              >
                ABOUT & BACKLINE
              </button>
            )}

            {headerMode !== "classic" && (
              <div className="lg:flex gap-3">
                {!isMobile && (
                  <ShareUrlButton
                    nadaUrl={`${bandUrl}?follow=true&utm_source=band_header&utm_medium=share`}
                    renderMode="button"
                    className="mb-0"
                  />
                )}
                <BandSocialIcons
                  band={band}
                  color="text-white"
                  margins="mr-3"
                  includeShareIcon={isMobile}
                />
              </div>
            )}
          </div>
        </div>

        <div className="relative py-2 pl-2 self-center flex gap-3 shrink-0 justify-end">
          {isFan ? null : user ? (
            <FollowComponent
              bandId={band.id}
              bandName={band.name}
              className="rounded-t shrink-0 whitespace-nowrap"
            />
          ) : (
            <FollowComponentLoggedOut
              bandId={band.id}
              bandName={band.name}
              className="rounded-t shrink-0 whitespace-nowrap"
            />
          )}
        </div>
      </div>

      {band.band_type === "venue" && (
        <Backline
          name={band.name}
          backlineOfferings={band.backline_offerings_html || ""}
          backlineOfferingsUpdatedAt={band.backline_offerings_updated_at}
          venueInfo={band.additional_info}
          location={band.location as any}
          isOpen={showBackline}
          onClose={() => setShowBackline(false)}
        />
      )}
    </>
  )
}

export default ScrollerBandHeader
